import { useState } from "react";

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement)?.value || "";
    if (message.length > 500) return;

    setStatus("sending");
    const data = new URLSearchParams();
    new FormData(form).forEach((val, key) => {
      data.append(key, val as string);
    });

    try {
      const res = await fetch("https://readdy.ai/api/form/d71iuf6uimmp87kg1iog", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section
      id="contact"
      className="relative py-28 overflow-hidden"
      style={{
        backgroundImage: `url("https://public.readdy.ai/ai/img_res/2124bb15f5565185162e5a368a2826e3.jpg")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-urban-dark/93" />

      <div className="relative z-10 max-w-7xl mx-auto px-8 md:px-16">
        <div className="flex items-center gap-4 mb-16">
          <div className="w-1 h-12 bg-red-600" />
          <div>
            <p className="font-rajdhani text-red-500 uppercase tracking-widest text-sm font-semibold">Reach Out</p>
            <h2 className="font-bebas text-5xl md:text-6xl text-white tracking-wider">Contact</h2>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-16">
          <div>
            <h3 className="font-bebas text-3xl text-white tracking-wider mb-4">
              Ready to <span className="text-red-500">Link Up?</span>
            </h3>
            <p className="font-rajdhani text-gray-400 text-base leading-relaxed mb-8">
              Whether you&apos;re looking for a collab, a sponsorship inquiry, press coverage, or just want to drop in the squad &mdash; hit me up. Old Man Murks reads every message personally.
            </p>

            <div className="space-y-5 mb-10">
              {[
                { icon: "ri-twitch-fill", label: "Streaming", value: "twitch.tv/OldManMurks", color: "text-purple-400" },
                { icon: "ri-youtube-fill", label: "YouTube", value: "youtube.com/@OldManMurksGaming", color: "text-red-500" },
                { icon: "ri-mail-fill", label: "Business Inquiries", value: "contact@oldmanmurks.gg", color: "text-red-400" },
                { icon: "ri-discord-fill", label: "Community", value: "discord.gg/murks-battalion", color: "text-indigo-400" },
              ].map((c) => (
                <div key={c.label} className="flex items-center gap-4 group">
                  <div className={`w-10 h-10 flex items-center justify-center bg-urban-card border border-urban-border rounded-sm text-lg ${c.color} group-hover:border-red-800/50 transition-colors`}>
                    <i className={c.icon} />
                  </div>
                  <div>
                    <p className="font-rajdhani text-gray-500 text-xs uppercase tracking-widest">{c.label}</p>
                    <p className="font-rajdhani text-white text-sm font-semibold">{c.value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-urban-card border border-urban-border rounded-sm p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="font-rajdhani text-green-400 text-xs uppercase tracking-widest font-semibold">Available for</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {["Sponsorships", "Collaborations", "Tournaments", "Content Creation", "Brand Deals", "Community Events"].map((tag) => (
                  <span
                    key={tag}
                    className="font-rajdhani text-xs text-gray-300 bg-urban-surface border border-urban-border px-3 py-1.5 rounded-sm uppercase tracking-wider"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div>
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-urban-card border border-urban-border rounded-sm p-8 space-y-5"
            >
              <h4 className="font-bebas text-2xl text-white tracking-wider mb-6">Send A Message</h4>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-rajdhani text-gray-400 text-xs uppercase tracking-widest block mb-2">Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="Your name"
                    className="w-full bg-urban-surface border border-urban-border text-white font-rajdhani text-sm px-4 py-3 rounded-sm focus:outline-none focus:border-red-700/60 placeholder-gray-600 transition-colors"
                  />
                </div>
                <div>
                  <label className="font-rajdhani text-gray-400 text-xs uppercase tracking-widest block mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="your@email.com"
                    className="w-full bg-urban-surface border border-urban-border text-white font-rajdhani text-sm px-4 py-3 rounded-sm focus:outline-none focus:border-red-700/60 placeholder-gray-600 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="font-rajdhani text-gray-400 text-xs uppercase tracking-widest block mb-2">Inquiry Type</label>
                <select
                  name="inquiry_type"
                  className="w-full bg-urban-surface border border-urban-border text-white font-rajdhani text-sm px-4 py-3 rounded-sm focus:outline-none focus:border-red-700/60 transition-colors cursor-pointer"
                >
                  <option value="general">General Message</option>
                  <option value="sponsorship">Sponsorship</option>
                  <option value="collaboration">Collaboration</option>
                  <option value="tournament">Tournament Invite</option>
                  <option value="press">Press / Media</option>
                  <option value="fan">Fan Message</option>
                </select>
              </div>

              <div>
                <label className="font-rajdhani text-gray-400 text-xs uppercase tracking-widest block mb-2">Subject</label>
                <input
                  type="text"
                  name="subject"
                  placeholder="What's this about?"
                  className="w-full bg-urban-surface border border-urban-border text-white font-rajdhani text-sm px-4 py-3 rounded-sm focus:outline-none focus:border-red-700/60 placeholder-gray-600 transition-colors"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="font-rajdhani text-gray-400 text-xs uppercase tracking-widest">Message *</label>
                  <span className={`font-rajdhani text-xs ${charCount > 450 ? "text-red-500" : "text-gray-500"}`}>{charCount}/500</span>
                </div>
                <textarea
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="Drop your message here..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full bg-urban-surface border border-urban-border text-white font-rajdhani text-sm px-4 py-3 rounded-sm focus:outline-none focus:border-red-700/60 placeholder-gray-600 transition-colors resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={status === "sending"}
                className="w-full bg-red-700 hover:bg-red-600 disabled:bg-red-900/50 text-white font-rajdhani font-bold uppercase tracking-widest text-sm py-4 rounded-sm transition-all duration-300 cursor-pointer whitespace-nowrap border border-red-600 disabled:cursor-not-allowed"
              >
                {status === "sending" ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" /> Sending...
                  </span>
                ) : "Send Message"}
              </button>

              {status === "success" && (
                <div className="bg-green-900/30 border border-green-700/40 rounded-sm p-4 flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-green-400 text-lg" />
                  <p className="font-rajdhani text-green-400 text-sm">Message sent! Old Man Murks will get back to you.</p>
                </div>
              )}
              {status === "error" && (
                <div className="bg-red-900/30 border border-red-700/40 rounded-sm p-4 flex items-center gap-3">
                  <i className="ri-error-warning-fill text-red-400 text-lg" />
                  <p className="font-rajdhani text-red-400 text-sm">Something went wrong. Try again or reach out directly on social media.</p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
